import React from 'react';
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Div from "@jumbo/shared/Div";
import DatePicker from "../../../shared/DatePicker";
import { getAPIDataParams, postApi } from '../../../utils/apiFunction';
import { textfieldDateFormat } from '../../../utils/dateFunctions';
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { ACCESS, ACCESS_SECTIONS } from '../../../utils/constants/access';
import { checkAccess } from '../../../utils/roleFunctions';
import SubmitButtonDisplayer from "../../../components/SubmitButtonDisplayer";
import ControlledTextField from "../../../components/ControlledTextField";
import SelectTextField from "../../../components/SelectTextField";

const NewBatchBroodstock = (props) => {
    const [completed, setCompleted] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [average, setAverage] = React.useState(0);
    const [total, setTotal] = React.useState(null);
    const [grade, setGrade] = React.useState(null);
    const [grades, setGrades] = React.useState([]);
    const [unitType, setUnitType] = React.useState(null);
    const [unitTypes, setUnitTypes] = React.useState([]);
    const [supplier, setSupplier] = React.useState(null);
    const [suppliers, setSuppliers] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [date] = React.useState(new Date());
    const [cropDate, setCropDate] = React.useState(new Date());
    const Swal = useSwalWrapper();
    const requiredEditAccess = { key: ACCESS_SECTIONS.FARMS, value: ACCESS.EDIT };

    React.useEffect(() => {
        const filterSuppliers = (data) => {
            const filteredSuppliers = (data || []).filter((s) => !!s.warehouseId);
            setSuppliers(filteredSuppliers);

            const farmSupplier = props.farm?.warehouseId
                ? filteredSuppliers.find((s) => String(s.warehouseId) === String(props.farm.warehouseId))
                : null;
            if (farmSupplier) {
                setSupplier(farmSupplier);
            }
        };

        const setDefaultGrade = (data) => {
            const hatcheryGrades = (data || []).filter((g) => g.isHatchery);
            setGrades(data || []);

            const lowestIdGrade = hatcheryGrades.reduce((lowest, current) => {
                if (!lowest || current.id < lowest.id) {
                    return current;
                }

                return lowest;
            }, null);

            if (lowestIdGrade) {
                setGrade(lowestIdGrade);
            }
        };

        const setDefaultUnitType = (data) => {
            setUnitTypes(data || []);

            const tankUnitType = (data || []).find((ut) => ut.description?.toLowerCase().includes('tank'));
            if (tankUnitType) {
                setUnitType(tankUnitType);
            }
        };

        getAPIDataParams('/Farms/Groups/OysterCloudSuppliers', filterSuppliers);
        getAPIDataParams('/Farms/Lookups/Grades', setDefaultGrade);
        getAPIDataParams('/Farms/Lookups/UnitTypes', setDefaultUnitType);
    }, [props.farm?.warehouseId]);

    const validate = () => {
        const errorMessages = [];
        if (!supplier) {
            errorMessages.push('Supplier is required');
        }
        if (!grade) {
            errorMessages.push('Grade is required');
        }
        if (!unitType) {
            errorMessages.push('Unit Type is required');
        }
        if (!total || Number(total) <= 0) {
            errorMessages.push('Total Units must be greater than 0');
        }
        if (!props.broodstockSelections || props.broodstockSelections.length === 0) {
            errorMessages.push('At least one broodstock group must be selected');
        }

        const formatErrorMessage = (array) => {
            let errString = '';
            array.forEach((a) => {
                errString = errString + a + '<br/>';
            });
            return errString;
        };

        if (errorMessages.length > 0) {
            Swal.fire({
                html: formatErrorMessage(errorMessages),
                icon: 'error',
                title: 'Oops...'
            });
            return;
        }

        submit();
    };

    const submit = async () => {
        setLoading(true);
        setCompleted(true);

        const broodstockSelections = props.broodstockSelections
            .map((x) => `${x.group?.id}:${x.gender}`)
            .join(',');
        const res = await postApi('/Farms/Groups/CreateBroodstockBatch', {
            SupplierId: supplier?.id,
            UnitTypeId: unitType?.id,
            GradeId: grade?.id,
            Average: average,
            WarehouseId: props.farm?.warehouseId,
            CropDate: textfieldDateFormat(cropDate),
            IsHatched: true,
            TotalUnits: total,
            Date: textfieldDateFormat(date),
            ExternalReceived: false,
            BroodstockSelections: broodstockSelections,
        });

        if (res.ok) {
            if (props.reload) {
                props.reload();
            }
            if (props.handleClose) {
                props.handleClose();
            }
        } else {
            setCompleted(false);
            setError('Error creating broodstock batch');
        }

        setLoading(false);
    };

    return (
        <Box
            component="form"
            sx={{
                '& .MuiTextField-root': { m: 1, width: '24ch' },
            }}
            noValidate
            autoComplete="off"
        >
            <Div>
                <Typography variant={"h2"}>Create New Hatchery Batch</Typography>
                <Typography variant={"body2"} mb={2}>
                    Using {props.broodstockSelections?.length || 0} selected broodstock groups
                </Typography>
                <Grid container spacing={0} mb={1}>
                    <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
                        <DatePicker date={cropDate} setDate={setCropDate} label="Crop Birth Date" disabled={completed} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={8} lg={8} xl={9}>
                        <Typography variant={"h4"}>Batch Name</Typography>
                        <Typography variant={"h3"}>{cropDate?.getFullYear() + '-HAT ' + (supplier ? supplier.name.replace(' Farm (Internal)', '') : '')}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <SelectTextField title="Supplier" state={supplier} checkID={supplier?.id} nameFunc={(gd) => gd.name.replace(' Farm (Internal)', '')} list={suppliers} setState={setSupplier} disabled={completed} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectTextField title="Grade" state={grade} checkID={grade?.id} nameFunc={(gd) => gd.description} list={grades.filter((g) => g.isHatchery)} setState={setGrade} disabled={completed} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <SelectTextField title="Unit Type" state={unitType} checkID={unitType?.id} nameFunc={(gd) => gd.description} list={unitTypes?.filter((gd) => gd.description?.toLowerCase().includes('screen')||gd.description?.toLowerCase().includes('tank'))} setState={setUnitType} disabled={completed} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <ControlledTextField title="Total Units" state={total} setState={setTotal} disabled={completed} number />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                        <ControlledTextField title="Average per Unit" state={average} setState={setAverage} disabled={completed} number />
                    </Grid>
                    <Grid item xs={12}>
                        <SubmitButtonDisplayer completed={completed} error={error} loading={loading} allowed={checkAccess(props.access, requiredEditAccess)} submitFunc={validate} handleClose={props.handleClose} />
                    </Grid>
                </Grid>
            </Div>
        </Box>
    );
};

export default NewBatchBroodstock;
